import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { api, getStoredToken, postAuthLogin } from '../api/client';

const AuthContext = createContext(null);

const TOKEN_KEY = 'luxistt_token';

/** Matches API `role_id` from /me */
export const ROLE_ADMIN = 1;
export const ROLE_OWNER = 2;
export const ROLE_USER = 3;
export const ROLE_FRONT_DESK = 4;

/** Coerce API `role_id` (sometimes string) for strict comparisons. */
export function normalizedRoleId(user) {
  if (user?.role_id == null || user.role_id === '') {
    return null;
  }
  const n = Number(user.role_id);
  return Number.isFinite(n) ? n : null;
}

/** Every numeric role id from /me (`role_ids` + primary `role_id`). */
export function roleIdSetForUser(user) {
  const s = new Set();
  if (Array.isArray(user?.role_ids)) {
    user.role_ids.forEach((r) => {
      const n = Number(r);
      if (Number.isFinite(n)) s.add(n);
    });
  }
  const p = normalizedRoleId(user);
  if (p != null) s.add(p);
  return s;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setToken = useCallback((token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const fetchUser = useCallback(async (token) => {
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const data = await api.get('/me', token);
      if (data && data.email_verified === false) {
        setToken(null);
        setUser(null);
        return null;
      }
      setUser(data);
      return data;
    } catch {
      setToken(null);
      setUser(null);
      return null;
    }
  }, [setToken]);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    fetchUser(token).finally(() => setLoading(false));
  }, [fetchUser]);

  const login = useCallback(async (email, password, opts = {}) => {
    let data;
    try {
      data = await postAuthLogin(email, password, opts);
    } catch (err) {
      const e = new Error(err.message || 'Login failed. Check your email and password.');
      e.status = err.status;
      e.data = err.data;
      throw e;
    }
    const token = data?.access_token ?? data?.data?.access_token;
    if (!token) {
      throw new Error(data?.message || 'Sign-in did not complete. Please try again.');
    }
    setToken(token);
    return fetchUser(token);
  }, [setToken, fetchUser]);

  const logout = useCallback(async () => {
    const token = getStoredToken();
    if (token) {
      try {
        await api.post('/logout', {}, token);
      } catch (_) {
        // ignore
      }
    }
    setToken(null);
    setUser(null);
  }, [setToken]);

  const refreshUser = useCallback(async () => {
    const token = getStoredToken();
    if (token) {
      return fetchUser(token);
    }
    return null;
  }, [fetchUser]);

  const permissions = useMemo(() => (Array.isArray(user?.permissions) ? user.permissions : []), [user?.permissions]);

  const hasPermission = useCallback((name) => permissions.includes(name), [permissions]);

  const staffAbilitiesByProperty = useMemo(() => {
    const raw = user?.staff_abilities_by_property;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    return null;
  }, [user?.staff_abilities_by_property]);

  /**
   * Front Desk: per-property ability strings from /me (null map = unrestricted / not front desk = all allowed).
   * When propertyId is empty, returns true if any assigned property grants the ability (for nav before a property is picked).
   */
  const canStaffAbilityForFrontDesk = useCallback(
    (propertyId, ability) => {
      if (
        staffAbilitiesByProperty == null
        || typeof staffAbilitiesByProperty !== 'object'
        || Array.isArray(staffAbilitiesByProperty)
      ) {
        return true;
      }
      const key = propertyId != null && propertyId !== '' ? String(propertyId) : null;
      if (key) {
        const list = staffAbilitiesByProperty[key];
        return Array.isArray(list) && list.includes(ability);
      }
      return Object.values(staffAbilitiesByProperty).some((list) => Array.isArray(list) && list.includes(ability));
    },
    [staffAbilitiesByProperty],
  );

  const roleIds = useMemo(() => roleIdSetForUser(user), [user]);
  const isFrontDeskUser = roleIds.has(ROLE_FRONT_DESK) || user?.role === 'Front Desk';
  /** Owners, guests, admins: always. Front Desk: needs account.profile on at least one assignment (see /me staff_abilities_by_property). */
  const canAccessProfilePage =
    !user || !isFrontDeskUser || canStaffAbilityForFrontDesk(null, 'account.profile');
  const canAccessFrontDesk =
    !!user &&
    (roleIds.has(ROLE_ADMIN) ||
      roleIds.has(ROLE_FRONT_DESK) ||
      roleIds.has(ROLE_OWNER) ||
      user?.role === 'Admin' ||
      user?.role === 'Front Desk' ||
      user?.role === 'Owner');

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    isOwner: roleIds.has(ROLE_OWNER) || user?.role === 'Owner',
    isUser: user?.role === 'User' || roleIds.has(ROLE_USER),
    isAdmin: roleIds.has(ROLE_ADMIN) || user?.role === 'Admin',
    isFrontDesk: roleIds.has(ROLE_FRONT_DESK) || user?.role === 'Front Desk',
    canAccessProfilePage,
    canAccessFrontDesk,
    canManage: user?.can_manage ?? (user?.role === 'Admin'),
    permissions,
    hasPermission,
    staffAbilitiesByProperty,
    canStaffAbilityForFrontDesk,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
